import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotificationProviders } from './api';

/**
 * Strona ustawień powiadomień (ADMIN-only). Pokazuje status providerów
 * SMS/email i WIDOCZNĄ informację, że SMS jest zaimplementowany ale
 * niewłączony — kupujący od razu rozumie, że to wymaga aktywacji
 * (decyzja użytkownika 2026-05-09).
 */
export function NotificationsSettingsPage(): ReactNode {
  const { t } = useTranslation();
  const { data, isLoading, error } = useNotificationProviders();

  return (
    <div className="space-y-6 max-w-3xl">
      <header>
        <h1 className="text-2xl font-semibold text-zinc-900">
          {t('settings.notifications.title')}
        </h1>
        <p className="text-sm text-zinc-600 mt-1">
          {t('settings.notifications.subtitle')}
        </p>
      </header>

      {isLoading && <div className="text-sm text-zinc-500">{t('common.loading')}</div>}
      {error && <div className="text-sm text-rose-600">{t('common.errorGeneric')}</div>}

      {data && (
        <>
          {/* SMS — kluczowa karta z informacją "implemented but not active". */}
          <section className="rounded-lg border border-amber-300 bg-amber-50 p-5">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-semibold text-amber-900">
                    {t('settings.notifications.sms.heading')}
                  </h2>
                  <ProviderBadge active={data.sms.active} provider={data.sms.provider} />
                </div>
                <p className="mt-2 text-sm text-amber-900">
                  {data.sms.activationNote}
                </p>
                <p className="mt-2 text-sm font-medium text-amber-900">
                  {data.sms.pricingNote}
                </p>
                <div className="mt-4 rounded-md bg-white/70 p-3 text-xs text-amber-900">
                  <div className="font-semibold">{t('settings.notifications.sms.howToActivate')}</div>
                  <ol className="mt-2 list-decimal space-y-1 pl-5">
                    <li>{t('settings.notifications.sms.step1')}</li>
                    <li>{t('settings.notifications.sms.step2')}</li>
                    <li>{t('settings.notifications.sms.step3')}</li>
                    <li>{t('settings.notifications.sms.step4')}</li>
                  </ol>
                </div>
                <div className="mt-3 text-xs text-amber-800">
                  {t('settings.notifications.sms.alternative')}
                </div>
              </div>
            </div>
          </section>

          {/* Email — informacja o trybie. */}
          <section className="rounded-lg border border-zinc-200 bg-white p-5">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-zinc-100 text-zinc-700">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-semibold text-zinc-900">
                    {t('settings.notifications.email.heading')}
                  </h2>
                  <ProviderBadge active={data.email.active} provider={data.email.provider} />
                </div>
                <p className="mt-2 text-sm text-zinc-600">
                  {data.email.active
                    ? t('settings.notifications.email.active')
                    : t('settings.notifications.email.inactive')}
                </p>
                {data.email.fromAddress && (
                  <div className="mt-2 text-xs text-zinc-500">
                    {t('settings.notifications.email.from')}:{' '}
                    <code className="rounded bg-zinc-100 px-1.5 py-0.5">
                      {data.email.fromAddress}
                    </code>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* In-app — zawsze aktywne (zapis do bazy). */}
          <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-5">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                  <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
                </svg>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-semibold text-emerald-900">
                    {t('settings.notifications.inApp.heading')}
                  </h2>
                  <span className="rounded-full bg-emerald-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-900">
                    {t('settings.notifications.statusActive')}
                  </span>
                </div>
                <p className="mt-2 text-sm text-emerald-900">
                  {t('settings.notifications.inApp.description')}
                </p>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function ProviderBadge({
  active,
  provider,
}: {
  active: boolean;
  provider: string;
}): ReactNode {
  const { t } = useTranslation();
  const cls = active
    ? 'bg-emerald-200 text-emerald-900'
    : 'bg-zinc-200 text-zinc-700';
  const label = active
    ? t('settings.notifications.statusActive')
    : t('settings.notifications.statusMock');
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}>
      {label} · {provider}
    </span>
  );
}
